<template>
  <div class="day-summary">
    <div v-if="bookings != undefined && bookings.length > 0">
      <div v-if="isDateBlocked" class="day-summary-alert">
        <i class="no no-c-warning"></i>
        Atrativos com bloqueio nessa data
      </div>

      <!-- Últimas reservas -->
      <b-card class="day-summary-section" no-body>
        <b-card-header
          class="day-summary-section-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center"
        >
          <span>Últimas reservas</span>
          <base-button
            @click="openBookingsModal"
            type="submit"
            class="btn btn-outline-info btn-sm mt-2 mt-sm-0 py-0 px-3"
          >
            ver todas
          </base-button>
        </b-card-header>
        <div class="day-summary-section-hint">
          As 10 reservas mais recentes do período (exceto canceladas)
        </div>
        <b-card-body class="pt-2">
          <div class="day-summary-bookings-list">
            <booking-list-card
              v-for="(booking, idx) in recentBookings"
              :key="booking.id || booking.code || idx"
              :booking="booking"
              compact
              class="mb-3"
              @open-booking-details="openBookingDetails($event.code)"
            />
          </div>
        </b-card-body>
      </b-card>

      <!-- Arrecadação -->
      <b-card
        v-if="totalOnline > 0 && hasChannel('Online', 'Localmente')"
        class="day-summary-section"
        no-body
      >
        <b-card-header class="day-summary-section-header">
          Arrecadação
        </b-card-header>
        <div class="day-summary-section-hint">
          Valores pagos no período, separados por canal online e local
        </div>
        <b-card-body>
          <div class="day-summary-compare">
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Online</h6>
              <div
                v-for="(item, idx) in channelItems('Online')"
                :key="'online-' + idx"
                class="day-summary-metric"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">
                  {{ formatCurrency(item.value) }}
                </span>
              </div>
            </div>
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Localmente</h6>
              <div
                v-for="(item, idx) in channelItems('Localmente')"
                :key="'local-' + idx"
                class="day-summary-metric"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">
                  {{ formatCurrency(item.value) }}
                </span>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Visitação -->
      <b-card
        v-if="hasChannel('Visitantes', 'Checkin')"
        class="day-summary-section"
        no-body
      >
        <b-card-header class="day-summary-section-header">
          Visitação
        </b-card-header>
        <div class="day-summary-section-hint">
          Perfil de visitantes e situação de check-in das reservas concluídas
        </div>
        <b-card-body>
          <div class="day-summary-compare">
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Visitantes</h6>
              <div
                v-for="(item, idx) in channelItems('Visitantes')"
                :key="'visit-' + idx"
                class="day-summary-metric"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">{{ item.value }}</span>
              </div>
            </div>
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Check-in</h6>
              <div
                v-for="(item, idx) in channelItems('Checkin')"
                :key="'checkin-' + idx"
                class="day-summary-metric"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Vendas -->
      <b-card
        v-if="
          (totalOnline > 0 || totalLocalmente > 0) &&
          hasChannel('Venda Remota', 'Venda Local')
        "
        class="day-summary-section"
        no-body
      >
        <b-card-header class="day-summary-section-header">
          Vendas
        </b-card-header>
        <div class="day-summary-section-hint">
          Quantidade de pessoas por origem da venda (remota ou local)
        </div>
        <b-card-body>
          <div class="day-summary-compare">
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Remota</h6>
              <div
                v-for="(item, idx) in channelItems('Venda Remota')"
                :key="'remote-' + idx"
                class="day-summary-metric"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">{{ item.value }}</span>
              </div>
            </div>
            <div class="day-summary-compare-col">
              <h6 class="day-summary-compare-title">Local</h6>
              <div
                v-for="(item, idx) in channelItems('Venda Local')"
                :key="'venda-local-' + idx"
                class="day-summary-metric"
                :class="{
                  'day-summary-metric--alert': isPendingMetric(item),
                }"
              >
                <span class="day-summary-metric-label">{{ item.title }}</span>
                <span class="day-summary-metric-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Outros canais -->
      <b-card
        v-for="channel in otherChannels"
        :key="channel.name"
        class="day-summary-section"
        no-body
      >
        <b-card-header class="day-summary-section-header">
          {{ channel.name }}
        </b-card-header>
        <div class="day-summary-section-hint">
          Indicadores adicionais deste resumo
        </div>
        <b-card-body>
          <div
            v-for="(item, idx) in channel.items"
            :key="channel.name + '-' + idx"
            class="day-summary-metric"
          >
            <span class="day-summary-metric-label">{{ item.title }}</span>
            <span class="day-summary-metric-value">{{ item.value }}</span>
          </div>
        </b-card-body>
      </b-card>

      <p
        v-if="totalOnline > 0 || totalLocalmente > 0"
        class="day-summary-footnote"
      >
        * Os valores deste resumo financeiro consideram apenas reservas
        concluídas (pagas e com check-in).
      </p>
    </div>

    <div v-else class="day-summary-empty">
      Não há reservas para a data {{ formattedSelectedDate }}.
    </div>

    <modal-tabela-reservas
      v-if="showModalTabelaReservas"
      :showModalTabelaReservas="showModalTabelaReservas"
      :transformedItemsArray="bookings"
      :showSeeDetails="true"
      @emitCloseModalTabelaReservas="closeBookingsModal"
    >
    </modal-tabela-reservas>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ModalTabelaReservas from '../Reports/ModalTabelaReservas.vue'
import BookingListCard from './BookingListCard.vue'
import moment from 'moment'

const KNOWN_CHANNELS = [
  'Online',
  'Localmente',
  'Visitantes',
  'Checkin',
  'Venda Remota',
  'Venda Local',
]

export default {
  name: 'DaySummary',

  components: {
    ModalTabelaReservas,
    BookingListCard,
  },

  props: {
    selectedDate: {
      type: String,
      default: '',
    },

    bookings: {
      type: Array,
      default: () => {
        return []
      },
    },
  },

  computed: {
    ...mapGetters('product', {
      poisList: 'getPois',
    }),

    formattedSelectedDate() {
      return this.formatDate(this.selectedDate, 'DD/MM/YYYY')
    },

    totalOnline() {
      return this.sumChannelValues('Online')
    },

    totalLocalmente() {
      return this.sumChannelValues('Localmente')
    },

    totalVisitantes() {
      return this.sumChannelValues('Visitantes')
    },

    isDateBlocked() {
      if (this.selectedDate) {
        return this.poisList
          .flatMap((i) => Object.keys(i.unavailability || {}))
          .some((i) => i == this.selectedDate)
      }

      return true
    },

    recentBookings() {
      return [...(this.bookings || [])]
        .filter((booking) => {
          const status = String(booking.status || '').toLowerCase()
          return status !== 'cancelled'
        })
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 10)
    },

    otherChannels() {
      return Object.keys(this.daySummary || {})
        .filter((name) => !KNOWN_CHANNELS.includes(name))
        .map((name) => {
          const items = this.channelItems(name).filter(
            (item) => Number(item.value) > 0
          )
          return { name, items }
        })
        .filter((channel) => channel.items.length > 0)
    },
  },

  watch: {
    async selectedDate(newValue) {
      if (newValue) {
        if (this.bookings != undefined && this.bookings.length > 0) {
          await this.formatDaySummary(this.bookings).then((res) => {
            this.daySummary = res
          })
        }
      }
    },

    async bookings(newValue) {
      if (newValue != undefined && newValue.length > 0) {
        await this.formatDaySummary(this.bookings).then((res) => {
          this.daySummary = res
        })
      }
    },
  },

  data() {
    return {
      daySummary: {},
      showModalTabelaReservas: false,
    }
  },

  methods: {
    ...mapActions('operacao', ['formatDaySummary']),

    openBookingDetails(bookingCode) {
      window.open(`/booking-details/${bookingCode}`, '_blank')
    },

    formatDate(date, format) {
      return moment(date).format(format)
    },

    formatCurrency(value) {
      const amount = Number(value) || 0
      return `R$ ${amount.toFixed(2).replace('.', ',')}`
    },

    channelItems(channelName) {
      const channel = this.daySummary && this.daySummary[channelName]
      if (!channel) return []
      if (Array.isArray(channel)) return channel
      return Object.keys(channel).map((title) => ({
        title,
        value: channel[title],
      }))
    },

    hasChannel(...names) {
      return names.some((name) => this.channelItems(name).length > 0)
    },

    sumChannelValues(channelName) {
      const items = this.channelItems(channelName)
      if (!items.length) return 0
      return items.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    },

    isPendingMetric(item) {
      if (!item) return false
      const title = String(item.title || '').toLowerCase()
      return (
        (title.includes('pendente') || title.includes('aguardando')) &&
        Number(item.value) > 0
      )
    },

    openBookingsModal() {
      this.showModalTabelaReservas = true
    },

    closeBookingsModal() {
      this.showModalTabelaReservas = false
    },
  },

  async mounted() {
    if (this.bookings != undefined && this.bookings.length > 0) {
      await this.formatDaySummary(this.bookings).then((res) => {
        this.daySummary = res
      })
    }
  },
}
</script>

<style>
.day-summary {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.day-summary-alert {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #d93051;
  font-size: 0.85rem;
  margin-bottom: 0.85rem;
}

.day-summary-section {
  border: 1px solid #e6eef2;
  border-radius: 0.75rem;
  box-shadow: none;
  margin-bottom: 1rem;
  overflow: hidden;
}

.day-summary-section-header {
  background: #f7fafc;
  border-bottom: 1px solid #e6eef2;
  color: #12576e;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.75rem 1rem;
}

.day-summary-section-hint {
  padding: 0.35rem 1rem 0.15rem;
  font-size: 0.75rem;
  font-weight: 400;
  color: #6c757d;
  background: #f7fafc;
  border-bottom: 1px solid #e6eef2;
}

.day-summary-section .card-body {
  padding: 0.85rem 1rem 1rem;
}

.day-summary-bookings-list {
  max-height: 30dvh;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 0;
}

.day-summary-compare {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.day-summary-compare-col {
  min-width: 0;
}

.day-summary-compare-title {
  color: #12576e;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.55rem;
}

.day-summary-metric {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.35rem 0;
  border-bottom: 1px dashed #e6eef2;
  color: #3b4654;
  font-size: 0.9rem;
}

.day-summary-metric:last-child {
  border-bottom: none;
}

.day-summary-metric-label {
  min-width: 0;
  word-break: break-word;
}

.day-summary-metric-value {
  flex-shrink: 0;
  font-weight: 600;
  color: #1f2a37;
  text-align: right;
}

.day-summary-metric--alert .day-summary-metric-label,
.day-summary-metric--alert .day-summary-metric-value {
  color: #d93051;
}

.day-summary-footnote {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0.25rem 0 1.5rem;
  line-height: 1.4;
}

.day-summary-empty {
  color: #6c757d;
  font-size: 0.95rem;
  padding: 0.5rem 0 1rem;
}

@media (max-width: 419.98px) {
  .day-summary-compare {
    grid-template-columns: 1fr;
  }
}
</style>
